import React, { useState } from "react";
import { Plus, Loader2, GitPullRequest } from "lucide-react";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Textarea } from "../ui/textarea";
import { Badge } from "../ui/badge";
import { Card, CardContent } from "../ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { changeImpactColors, formatDate } from "./constants";

export function ChangesTab({ contractId, changes, fetchData, expanded, submitForm, updateItem }) {
  const [form, setForm] = useState({ change_title: "", description: "", impact: "low" });
  const [showForm, setShowForm] = useState(false);
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    setSaving(true);
    await submitForm(
      `/contracts/${contractId}/change-control`,
      form,
      () => setForm({ change_title: "", description: "", impact: "low" }),
      setShowForm
    );
    setSaving(false);
  };

  return (
    <div className="space-y-3">
      <div className="flex justify-between items-center">
        <p className="text-sm text-slate-500">{changes.length} change(s)</p>
        <Button size="sm" variant="outline" className="gap-1 rounded-sm" onClick={() => setShowForm(true)} data-testid="add-change-btn"><Plus size={14} />Log Change</Button>
      </div>

      {showForm && (
        <Card className="border-cyan-200">
          <CardContent className="pt-4 space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1">
                <Label className="text-xs">Change Title</Label>
                <Input value={form.change_title} onChange={(e) => setForm({ ...form, change_title: e.target.value })} className="rounded-sm h-9" placeholder="Brief title" data-testid="change-title-input" />
              </div>
              <div className="space-y-1">
                <Label className="text-xs">Impact</Label>
                <Select value={form.impact} onValueChange={(v) => setForm({ ...form, impact: v })}>
                  <SelectTrigger className="rounded-sm h-9"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="low">Low</SelectItem>
                    <SelectItem value="medium">Medium</SelectItem>
                    <SelectItem value="high">High</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="space-y-1">
              <Label className="text-xs">Description</Label>
              <Textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} rows={3} className="rounded-sm" placeholder="Describe the change..." data-testid="change-description-input" />
            </div>
            <div className="flex gap-2 justify-end">
              <Button size="sm" variant="ghost" onClick={() => setShowForm(false)}>Cancel</Button>
              <Button size="sm" disabled={saving || !form.change_title || !form.description} onClick={handleSave} data-testid="save-change-btn">{saving ? <Loader2 size={14} className="animate-spin" /> : "Save"}</Button>
            </div>
          </CardContent>
        </Card>
      )}

      <div className={`space-y-2 overflow-y-auto ${expanded ? "max-h-[calc(100vh-350px)]" : "max-h-[400px]"}`}>
        {changes.map((entry) => (
          <div key={entry.id} className="p-3 border rounded-sm">
            <div className="flex items-center justify-between mb-1">
              <span className="font-medium text-sm">{entry.change_title}</span>
              <div className="flex items-center gap-2">
                <Badge className={`rounded-sm text-xs ${changeImpactColors[entry.impact]}`}>{entry.impact}</Badge>
                <Select defaultValue={entry.status} onValueChange={(v) => updateItem(`/change-control/${entry.id}`, { status: v })}>
                  <SelectTrigger className="h-7 w-28 rounded-sm text-xs"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {["proposed", "approved", "rejected", "implemented"].map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <p className="text-sm text-slate-600">{entry.description}</p>
            <p className="text-xs text-slate-400 mt-1">{entry.requested_by_name} · {formatDate(entry.created_at)}</p>
          </div>
        ))}
        {changes.length === 0 && <p className="text-sm text-slate-400 text-center py-6">No change control entries</p>}
      </div>
    </div>
  );
}
